class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);

    FacetFiltersForm.ensureInStockChecked();
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      // Not awaited: popstate is browser navigation, not a shopper applying a
      // filter, so it doesn't dispatch a filter analytics event (see
      // dispatchFilterAnalyticsEvent). The handler keeps a failed fetch visible
      // in the console without an unhandled rejection.
      FacetFiltersForm.renderPage(searchParams, null, false).catch((error) => {
        console.warn('FacetFiltersForm: popstate render failed', error);
      });
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  /**
   * @param {string} searchParams
   * @param {Event} [event]
   * @param {boolean} [updateURLHash]
   * @returns {Promise<string>} Resolves with the last rendered section's fetched/cached
   *   HTML once every section has finished rendering (from fetch or cache). Callers
   *   that only care "has this settled" can ignore the resolved value; callers that
   *   need the real post-render product count (see dispatchFilterAnalyticsEvent) read
   *   it from the DOM after this resolves, since #product-grid's data-results-count
   *   attribute reflects whatever HTML this promise just rendered.
   */
  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    const renderPromises = sections.map((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      return FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);

    return Promise.all(renderPromises).then((renderedHtml) => renderedHtml[renderedHtml.length - 1]);
  }

  /**
   * @param {string} url
   * @param {Event} [event]
   * @returns {Promise<string>} Resolves with the fetched HTML once it has rendered.
   */
  static renderSectionFromFetch(url, event) {
    return fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);

        setTimeout(() => {
          FacetFiltersForm.ensureInStockChecked();
        }, 100);

        return html;
      });
  }

  /**
   * @param {(element: {html: string, url: string}) => boolean} filterDataUrl
   * @param {Event} [event]
   * @returns {Promise<string>} Resolves with the cached HTML once it has rendered.
   *   Wrapped in a promise (the render itself is synchronous) so callers of
   *   renderPage() get one consistent awaitable type regardless of which branch ran.
   */
  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);

    setTimeout(() => {
      FacetFiltersForm.ensureInStockChecked();
    }, 100);

    return Promise.resolve(html);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });

    // refreshes wishlist icon
    if(window._swat) {
      window._swat.initializeActionButtons('#product-grid');
    }
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    FacetFiltersForm.ensureInStockChecked();

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  /**
   * Fallback filter type when no label exists for a param name (see
   * `buildProductFiltersPayload`) — reads a Shopify filter query param name and
   * derives a name from its shape, e.g. `filter.v.option.color` -> `color`,
   * `filter.p.product_type` -> `product_type`, `filter.v.availability` ->
   * `availability`. Falls back to the raw param name for anything that doesn't
   * match the documented `filter.v.*`/`filter.p.*` convention (see
   * documentation/systems/facets.md).
   * @param {string} paramName
   * @returns {string}
   */
  static getFilterType(paramName) {
    const match = paramName.match(/^filter\.(?:v\.option\.|v\.|p\.)(.+)$/);
    return match ? match[1] : paramName;
  }

  /**
   * Maps a Liquid `sort_by` value (e.g. `price-ascending`, `manual`) to the
   * standard event's `sortKey` enum. `shopify:collection:update` and
   * `shopify:search:update` use DIFFERENT enums for this field, confirmed against
   * Shopify's own docs — sending the wrong one fails schema validation the same
   * way the wrong `productFilters` shape does:
   * - Collection (`shopify:collection:update`): `BEST_SELLING`/`COLLECTION_DEFAULT`/
   *   `CREATED`/`ID`/`MANUAL`/`PRICE`/`RELEVANCE`/`TITLE` — see
   *   https://shopify.dev/docs/api/storefronts/themes/best-practices/standard-events-and-actions/events/collection-update
   * - Search (`shopify:search:update`): `RELEVANCE`/`PRICE` only — see
   *   https://shopify.dev/docs/api/storefronts/themes/best-practices/standard-events-and-actions/events/search-update
   *
   * Neither enum has ascending/descending variants, so direction is unavoidably
   * lost for this standard event specifically — `Shopify.analytics.publish`'s
   * `sort_key` (see `publishAnalyticsEvent`) keeps the full, undegraded Liquid
   * value (`price-ascending`, not just `PRICE`) for anything that needs direction.
   * Returns `undefined` for a value with no enum equivalent for the given page
   * type (e.g. `manual` on a search page — search has no `MANUAL`), so the field
   * can be omitted from the event payload rather than sent as an invalid string.
   * @param {string|null} sortBy
   * @param {'collection'|'search'} pageType
   * @returns {string|undefined}
   */
  static getStandardSortKey(sortBy, pageType) {
    if (!sortBy) return undefined;

    if (pageType === 'search') {
      const searchSortKeyMap = { relevance: 'RELEVANCE', 'price-ascending': 'PRICE', 'price-descending': 'PRICE' };
      return searchSortKeyMap[sortBy];
    }

    const collectionSortKeyMap = {
      manual: 'MANUAL',
      'best-selling': 'BEST_SELLING',
      'title-ascending': 'TITLE',
      'title-descending': 'TITLE',
      'price-ascending': 'PRICE',
      'price-descending': 'PRICE',
      'created-ascending': 'CREATED',
      'created-descending': 'CREATED',
      relevance: 'RELEVANCE',
    };
    return collectionSortKeyMap[sortBy];
  }

  /**
   * Strips noise out of a submitted query string before it's used to build the
   * fetch URL, `history.pushState`, or an analytics payload:
   * - Any param with an empty value (e.g. `filter.v.price.gte=`, `filter.v.price.lte=`
   *   from an untouched price-range input — every form submission includes these
   *   inputs whether or not a shopper typed into them, since `createSearchParams`
   *   serializes the whole `<form>` via `FormData`, not just changed fields).
   * - `sort_by` specifically when its value equals `#product-grid`'s
   *   `data-default-sort` — the sort `<select>` always submits *some* value, so a
   *   shopper who never touched sort still submits `sort_by=<the current default>`
   *   indistinguishably from one who explicitly re-selected the default. Both read
   *   as "no real sort preference," so both are dropped, matching Shopify's own
   *   canonical-URL convention of omitting a sort param that matches the default.
   * @param {string} searchParams
   * @param {Element} [productGrid] - Read for `data-default-sort`. Falls back to
   *   `document.getElementById('product-grid')` when omitted.
   * @returns {string}
   */
  static cleanSearchParams(searchParams, productGrid = document.getElementById('product-grid')) {
    const defaultSort = productGrid?.dataset.defaultSort || null;
    const params = new URLSearchParams(searchParams);

    Array.from(params.entries()).forEach(([key, value]) => {
      if (value === '') {
        params.delete(key);
        return;
      }
      if (key === 'sort_by' && defaultSort && value === defaultSort) {
        params.delete(key);
      }
    });

    return params.toString();
  }

  /**
   * Reads `#product-grid`'s `data-filter-labels` JSON attribute — a
   * `{paramName: label}` map built server-side by `snippets/filter-labels-json.liquid`
   * from the same `filter.label`/`filter.param_name` Liquid objects the visible
   * filter UI renders from. This is the merchant-facing label a shopper actually
   * saw ("Color"), not a name derived from the URL param's shape — which differs
   * between a variant-option filter (`filter.v.option.color`) and a metafield
   * filter (`filter.p.m.custom.color`) even when both mean "Color" to a shopper.
   * @param {Element} productGrid
   * @returns {Record<string, string>}
   */
  static getFilterLabels(productGrid) {
    try {
      return JSON.parse(productGrid.dataset.filterLabels || '{}');
    } catch (error) {
      console.warn('FacetFiltersForm: failed to parse data-filter-labels', error);
      return {};
    }
  }

  /**
   * Builds the flat `{key, value}[]` filter payload for `Shopify.analytics.publish`
   * (see `publishAnalyticsEvent`/`dispatchFilterAnalyticsEvent` below) — the
   * Customer Privacy-aware channel this theme fully controls both ends of, where a
   * simplified shape is fine. This is NOT what `shopify:collection:update`/
   * `shopify:search:update`'s `productFilters` expects — that standard event has
   * its own fixed schema (Shopify's `ProductFilter` type), built separately by
   * `buildStandardProductFilters` below. Sort (`sort_by`, `section_id`, `page`) is
   * not a product filter, so it's excluded. Filter types in
   * `FacetFiltersForm.excludedFilterTypes` (matched against the raw param name, not
   * the label — see that field's own comment) are excluded too.
   * @param {string} searchParams
   * @param {Record<string, string>} filterLabels - From `getFilterLabels`.
   * @returns {Array<{key: string, value: string}>}
   */
  static buildProductFiltersPayload(searchParams, filterLabels) {
    const params = new URLSearchParams(searchParams);
    const productFilters = [];

    params.forEach((value, key) => {
      if (!key.startsWith('filter.') || value === '') return;
      if (FacetFiltersForm.excludedFilterTypes.some((excluded) => key.includes(excluded))) return;
      const filterLabel = filterLabels[key] || FacetFiltersForm.getFilterType(key);
      productFilters.push({ key: filterLabel, value });
    });

    return productFilters;
  }

  /**
   * Builds `productFilters` in Shopify's actual standard-event shape (the
   * `ProductFilter` type from `@shopify/events`) for `shopify:collection:update`/
   * `shopify:search:update` — a fixed union of named properties
   * (`variantOption`, `productType`, `tag`, `productVendor`, `productMetafield`,
   * `variantMetafield`, `available`, `price`, `category`), NOT the flat
   * `{key, value}` shape `buildProductFiltersPayload` builds for
   * `Shopify.analytics.publish`. Sending the wrong shape here fails standard-event
   * schema validation (e.g. the Standard Events Inspector's "expected at least one
   * filter property" error) even though it "looks like" a filter object.
   *
   * Maps directly off Shopify's documented filter query-param convention (see
   * https://shopify.dev/docs/storefronts/themes/navigation-search/filtering/storefront-filtering#url-parameter-structure),
   * not off `filter.type`/`filter.label`, since the param name alone fully encodes
   * which `ProductFilter` property applies:
   * - `filter.v.option.<name>` -> `variantOption: {name, value}`
   * - `filter.p.tag` -> `tag: value`
   * - `filter.p.product_type` -> `productType: value`
   * - `filter.p.vendor` -> `productVendor: value`
   * - `filter.p.m.<namespace>.<key>` -> `productMetafield: {namespace, key, value}`
   * - `filter.v.m.<namespace>.<key>` -> `variantMetafield: {namespace, key, value}`
   * - `filter.v.availability` -> `available: boolean` (excluded — see
   *   `excludedFilterTypes` — so this case never actually reaches this method today)
   * - `filter.v.price.gte`/`.lte` -> `price: {min}`/`price: {max}`
   *
   * A price range filter's `.gte` and `.lte` describe the SAME logical filter, so
   * both must merge into one `{price: {min, max}}` entry, not two separate ones —
   * the merge is keyed by everything before `.gte`/`.lte` in the param name.
   *
   * Any param that doesn't match one of the shapes above is dropped rather than
   * guessed at, since an incorrect/empty `ProductFilter` object fails validation
   * the same way a missing one does.
   * @param {string} searchParams
   * @returns {Array<import('@shopify/events').ProductFilter>}
   */
  static buildStandardProductFilters(searchParams) {
    const params = new URLSearchParams(searchParams);
    const priceFilters = new Map();
    const filters = [];

    params.forEach((value, key) => {
      if (!key.startsWith('filter.') || value === '') return;
      if (FacetFiltersForm.excludedFilterTypes.some((excluded) => key.includes(excluded))) return;

      let match;
      if ((match = key.match(/^filter\.v\.option\.(.+)$/))) {
        filters.push({ variantOption: { name: match[1], value } });
      } else if (key === 'filter.p.tag') {
        filters.push({ tag: value });
      } else if (key === 'filter.p.product_type') {
        filters.push({ productType: value });
      } else if (key === 'filter.p.vendor') {
        filters.push({ productVendor: value });
      } else if ((match = key.match(/^filter\.p\.m\.([^.]+)\.(.+)$/))) {
        filters.push({ productMetafield: { namespace: match[1], key: match[2], value } });
      } else if ((match = key.match(/^filter\.v\.m\.([^.]+)\.(.+)$/))) {
        filters.push({ variantMetafield: { namespace: match[1], key: match[2], value } });
      } else if ((match = key.match(/^filter\.v\.price\.(gte|lte)$/))) {
        const priceKey = key.slice(0, key.lastIndexOf('.'));
        const price = priceFilters.get(priceKey) || {};
        price[match[1] === 'gte' ? 'min' : 'max'] = Number(value);
        priceFilters.set(priceKey, price);
      }
      // filter.v.availability is excluded above (excludedFilterTypes), and any
      // param outside this convention is silently dropped rather than guessed at.
    });

    priceFilters.forEach((price) => filters.push({ price }));

    return filters;
  }

  /**
   * Publishes a custom event through Shopify's Customer Privacy-aware analytics
   * channel. Custom pixels and app pixels on the store receive the event. The DOM
   * event from `dispatchFilterAnalyticsEvent` does not reach pixels. Standard
   * storefront DOM events fire without regard to tracking consent. They exist for
   * on-page reactions, not analytics. `Shopify.analytics.publish` respects the
   * buyer's consent choice and reaches pixels. See
   * https://shopify.dev/docs/api/web-pixels-api/emitting-data.
   *
   * Event names are namespaced `frances_valentine:*` because Shopify reserves
   * unprefixed/standard event names for its own use — publishing a bare name is
   * rejected.
   *
   * `window.Shopify.analytics` is populated by Shopify's own storefront runtime
   * (not something this theme loads), so its presence is checked defensively rather
   * than assumed.
   * @param {string} eventName - Without the `frances_valentine:` prefix, e.g. `'collection_filtered'`.
   * @param {Record<string, unknown>} eventData - JSON-serializable. Shared with pixel
   *   subscribers via the event's `customData` field.
   */
  static publishAnalyticsEvent(eventName, eventData) {
    // Same debug flag/URL param as assets/standard-events.js, so both channels
    // (the DOM event there, the Customer Privacy publish here) toggle together.
    const debug =
      window.themeStandardEventsDebug ||
      new URLSearchParams(window.location.search).get('standard_events_debug') === '1';
    if (debug) console.log(`FacetFiltersForm: Shopify.analytics.publish('frances_valentine:${eventName}', ...)`, eventData);

    if (typeof window.Shopify?.analytics?.publish !== 'function') {
      if (debug) console.warn('FacetFiltersForm: window.Shopify.analytics.publish is unavailable — not published');
      return;
    }

    // Promise.resolve() guards against a publish() implementation that returns a
    // non-thenable value. Without it, `.then` would throw and stop the caller's loop.
    Promise.resolve(window.Shopify.analytics.publish(`frances_valentine:${eventName}`, eventData))
      .then((result) => {
        if (debug) console.log(`FacetFiltersForm: frances_valentine:${eventName} publish result`, result);
      })
      .catch((error) => {
        console.warn(`FacetFiltersForm: failed to publish frances_valentine:${eventName}`, error);
      });
  }

  /**
   * Diffs two `productFilters` payloads (see `buildProductFiltersPayload`) and
   * returns each `{key, value}` pair that changed state — either newly applied
   * (present in `next`, absent from `previous`) or just removed (present in
   * `previous`, absent from `next`), tagged with which.
   *
   * A diff, rather than the full current filter set, is what a pixel actually wants
   * per filter-change event: "the customer applied color:Black" is one fact,
   * distinct from "these are the five filters currently active."
   * @param {Array<{key: string, value: string}>} previous
   * @param {Array<{key: string, value: string}>} next
   * @returns {Array<{key: string, value: string, action: 'applied'|'removed'}>}
   */
  static diffProductFilters(previous, next) {
    // JSON-encoded, not a plain "`${key} ${value}`" join: `key` is now a
    // merchant-editable filter label (see buildProductFiltersPayload), not a fixed
    // short code, so a plain-space join could collide -- e.g. {key:'Color',
    // value:'Black Multi'} and a hypothetical {key:'Color Black', value:'Multi'}
    // would join to the same string. JSON.stringify keeps them distinct.
    const toId = ({ key, value }) => JSON.stringify({ key, value });
    const previousIds = new Set(previous.map(toId));
    const nextIds = new Set(next.map(toId));

    const applied = next
      .filter((filter) => !previousIds.has(toId(filter)))
      .map((filter) => ({ ...filter, action: 'applied' }));
    const removed = previous
      .filter((filter) => !nextIds.has(toId(filter)))
      .map((filter) => ({ ...filter, action: 'removed' }));

    return [...applied, ...removed];
  }

  /**
   * Dispatches `shopify:collection:update` or `shopify:search:update` (Shopify's
   * standard storefront events, see assets/standard-events.js) with the filters a
   * shopper just applied, so page-level code can react without parsing the DOM. This
   * does NOT reach custom pixels on its own — see `publishAnalyticsEvent` above for
   * the call that actually does.
   *
   * Dispatched immediately (before `renderPromise` settles), carrying a `promise`
   * that resolves once rendering actually completes — matching the standard event's
   * own contract, where the event announces a pending update and the promise
   * represents its in-flight result. `resultsCount` is read fresh from
   * `#product-grid`'s `data-results-count` attribute after `renderPromise` resolves
   * (`renderPage()`'s fetched/cached HTML has been rendered into the DOM by then, so
   * the attribute reflects the real fetched count, not the previous render's).
   *
   * Dynamically imported so a CDN failure to load `@shopify/events` (declared in
   * snippets/import-map.liquid) never breaks filtering itself.
   *
   * Also publishes analytics through `publishAnalyticsEvent` — the Customer
   * Privacy-aware channel a custom pixel actually needs, independent of the DOM
   * event above:
   * - `frances_valentine:item_list_filtered` — once per filter that actually changed
   *   (see `diffProductFilters`).
   * - `frances_valentine:item_list_sorted` — once, only when `sort_by` itself changed
   *   from the previous submission. Independent of `item_list_filtered`: a submission
   *   can change both (both events fire), just sort (only `item_list_sorted` fires),
   *   or just filters (only `item_list_filtered` fires).
   * @param {string} searchParams
   * @param {string} previousSearchParams - The query params in effect immediately
   *   before this filter change, captured by the caller before `renderPage` pushes
   *   the new URL. Used to diff which filter(s) were just applied/removed, and
   *   whether sort changed.
   * @param {Promise<string>} renderPromise - The promise `renderPage()` returned for
   *   this same filter change.
   */
  static dispatchFilterAnalyticsEvent(searchParams, previousSearchParams, renderPromise) {
    // Attach a rejection handler before any early return. Without one, a failed
    // fetch surfaces as an unhandled rejection when this function returns early.
    renderPromise.catch(() => {});

    const productGrid = document.getElementById('product-grid');
    if (!productGrid) return;

    const filterLabels = FacetFiltersForm.getFilterLabels(productGrid);
    const productFilters = FacetFiltersForm.buildProductFiltersPayload(searchParams, filterLabels);
    const previousProductFilters = FacetFiltersForm.buildProductFiltersPayload(previousSearchParams, filterLabels);
    const changedFilters = FacetFiltersForm.diffProductFilters(previousProductFilters, productFilters);
    const { pageType, collectionId, collectionHandle, collectionUrl, searchTerms, defaultSort } = productGrid.dataset;
    // cleanSearchParams (see onSubmitForm/onActiveFilterClick) strips sort_by from
    // the URL whenever it matches the default, so a missing sort_by here means "the
    // default sort is active," not "no sort" — fall back to the real default sort
    // key rather than reporting null, which would lose that information for a
    // pixel (e.g. `null` can't tell "still on Featured" apart from "no sort exists").
    const sortKey = new URLSearchParams(searchParams).get('sort_by') || defaultSort || null;
    const previousSortKey = new URLSearchParams(previousSearchParams).get('sort_by') || defaultSort || null;

    const settlementPromise = renderPromise.then(
      () => ({
        productsCount: Number(document.getElementById('product-grid')?.dataset.resultsCount) || 0,
      }),
      (error) => {
        throw error;
      }
    );
    // Nothing else awaits this promise, so an unhandled rejection would surface as a
    // console error whenever a filter fetch fails (e.g. an aborted/superseded request).
    settlementPromise.catch(() => {});

    // Shared across both events below — the page/collection/search context a
    // shopper acted within, not what changed. default_sort_key lets a pixel
    // compare sort_key against the actual default itself, rather than hardcoding
    // an assumption about what "the default" is per collection/search page.
    const context = {
      page_type: pageType || 'collection',
      collection_id: collectionId || null,
      collection_handle: collectionHandle || null,
      collection_url: collectionUrl || null,
      search_terms: searchTerms || null,
      full_url: window.location.href,
      default_sort_key: defaultSort || null,
    };

    changedFilters.forEach((filter) => {
      FacetFiltersForm.publishAnalyticsEvent('item_list_filtered', {
        filter_type: filter.key,
        filter_value: filter.value,
        action: filter.action,
        sort_key: sortKey,
        ...context,
      });
    });

    if (sortKey !== previousSortKey) {
      FacetFiltersForm.publishAnalyticsEvent('item_list_sorted', {
        sort_key: sortKey,
        previous_sort_key: previousSortKey,
        ...context,
      });
    }

    // Built separately from the flat `productFilters`/`sortKey` above: the
    // standard event has its own fixed schema (Shopify's `ProductFilter` type and
    // `ProductCollectionSortKeys` enum) that a flat `{key, value}` pair and a raw
    // Liquid sort_by string both fail validation against — see
    // buildStandardProductFilters/getStandardSortKey for why.
    const standardProductFilters = FacetFiltersForm.buildStandardProductFilters(searchParams);
    const standardSortKey = FacetFiltersForm.getStandardSortKey(sortKey, pageType === 'search' ? 'search' : 'collection');

    import('@shopify/events')
      .then(({ CollectionUpdateEvent, SearchUpdateEvent }) => {
        if (pageType === 'search') {
          document.dispatchEvent(
            new SearchUpdateEvent({
              search: {
                query: searchTerms || '',
                productFilters: standardProductFilters,
                sortKey: standardSortKey,
              },
              promise: settlementPromise.then(({ productsCount }) => ({ totalCount: productsCount })),
            })
          );
          return;
        }

        document.dispatchEvent(
          new CollectionUpdateEvent({
            collection: {
              id: collectionId && /^\d+$/.test(collectionId) ? collectionId : null,
              handle: collectionHandle || '',
              productsCount: Number(productGrid.dataset.resultsCount) || 0,
            },
            productFilters: standardProductFilters,
            sortKey: standardSortKey,
            promise: settlementPromise,
          })
        );
      })
      .catch((error) => {
        console.warn('FacetFiltersForm: failed to dispatch filter standard event', error);
      });
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    // Strips blank inputs (e.g. an untouched price-range field) and a sort_by that
    // matches the collection/search default before it ever reaches the fetch URL
    // or history.pushState — see cleanSearchParams for why both count as noise.
    searchParams = FacetFiltersForm.cleanSearchParams(searchParams);
    // Captured before renderPage() runs — renderPage() pushes the new params onto
    // the URL synchronously, so this is the last point where location.search still
    // reflects the filter state as it was before this submission.
    const previousSearchParams = window.location.search.slice(1);
    const renderPromise = FacetFiltersForm.renderPage(searchParams, event);
    FacetFiltersForm.dispatchFilterAnalyticsEvent(searchParams, previousSearchParams, renderPromise);
  }

  static ensureInStockChecked() {
    const forms = document.querySelectorAll('facet-filters-form form');
    forms.forEach((form) => {
      const availabilityInputs = form.querySelectorAll('input[name*="filter.v.availability"]');
      availabilityInputs.forEach((input) => {
        const label = input.closest('label') || input.parentElement;
        const labelText = label ? label.textContent.trim().toLowerCase() : '';
        const container = input.closest('.facet-checkbox, .mobile-facets__label');
        const textElement = container ? container.querySelector('.facet-checkbox__text-label, .facet-checkbox__text') : null;
        const text = textElement ? textElement.textContent.trim().toLowerCase() : '';

        if ((input.value === '1' || labelText.includes('in stock') || text.includes('in stock')) && !input.checked) {
          input.checked = true;
        }
      });
    });
  }

  onSubmitHandler(event) {
    event.preventDefault();

    FacetFiltersForm.ensureInStockChecked();

    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    let url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    // See onSubmitForm for why this is applied — url_to_remove is Shopify-generated,
    // not a raw form submission, but cleaning it too keeps behavior consistent and
    // costs nothing when there's nothing to clean.
    url = FacetFiltersForm.cleanSearchParams(url);
    // See onSubmitForm for why this is captured before renderPage() runs.
    const previousSearchParams = window.location.search.slice(1);
    const renderPromise = FacetFiltersForm.renderPage(url);
    FacetFiltersForm.dispatchFilterAnalyticsEvent(url, previousSearchParams, renderPromise);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
// Raw query-param substrings excluded from analytics (see buildProductFiltersPayload
// — matched against the param name, e.g. `filter.v.availability`, not the resolved
// label, since a label can be renamed in the theme editor's filter settings while
// the param name Shopify generates cannot). These are forced/sticky filters a
// shopper never actually chose, not real filter changes. `v.availability` is
// force-checked by ensureInStockChecked() before every submit — it would otherwise
// show up as a spurious `applied` event on the first filter action of any page
// session where the URL didn't already carry it, since the diff in
// dispatchFilterAnalyticsEvent compares against the live URL bar, not form state.
FacetFiltersForm.excludedFilterTypes = ['v.availability'];
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
